<script>
import { h } from "vue";
import SecondaryPageLayout from "@/Layouts/SecondaryPageLayout.vue";

export default {
    layout: (_, page) =>
        h(
            SecondaryPageLayout,
            {
                title: "Contact Gift Hero",
                subtitle: "Stay connected. Get support.",
            },
            () => page,
        ),
};
</script>

<script setup>
import { Head } from "@inertiajs/vue3";
import ContactCtaCard from "@/Components/Brand/ContactCtaCard.vue";

defineProps({
    social_links: {
        type: Object,
        required: true,
    },
});

const socialHandles = [
    {
        label: "@gift_hero",
        key: "instagram",
        icon: "images/brand/social-1.svg",
        ariaLabel: "Gift Hero on Instagram",
    },
    {
        label: "@gifthero",
        key: "facebook",
        icon: "images/brand/social-2.svg",
        ariaLabel: "Gift Hero on Facebook",
    },
];
</script>

<template>
    <Head>
        <title>Contact Us</title>
        <meta name="description" content="How to get in touch with Gift Hero" />
        <meta property="og:title" content="Contact Gift Hero" />
        <meta
            property="og:description"
            content="How to get in touch with Gift Hero"
        />
        <meta property="og:url" :content="route('contact-us')" />
        <link rel="canonical" :href="route('contact-us')" />
    </Head>

    <h2
        class="font-brand-display text-[36px] font-black italic uppercase leading-[1.15] tracking-[-0.02em] text-[#102a3d] md:text-[45px]"
    >
        Don’t be shy! Let’s chat.
    </h2>
    <p class="mt-1 text-[18px] leading-[1.3] text-[#102a3d] md:text-[20px]">
        We love to hear from our users! Your insights help us constantly
        improve.
    </p>

    <div class="mt-9 grid gap-[30px] md:grid-cols-2">
        <ContactCtaCard
            href="mailto:feedback@gifthero.com"
            bg-class="bg-[#ffaf03]"
            image="images/brand/contact-cta-questions.webp"
        >
            For questions, comments or suggestions
        </ContactCtaCard>

        <ContactCtaCard
            href="mailto:support@gifthero.com"
            bg-class="bg-[#48057c]"
            image="images/brand/contact-cta-support.webp"
        >
            For technical support, bugs or mobile/site issues
        </ContactCtaCard>

        <ContactCtaCard
            href="mailto:marketing@gifthero.com"
            bg-class="bg-brand-red"
            image="images/brand/contact-cta-press.webp"
        >
            For media or press inquiries
        </ContactCtaCard>

        <!-- Social tile: navy with the ghost mark bleeding off the right edge -->
        <div
            class="relative flex min-h-[216px] flex-col justify-between gap-6 overflow-hidden rounded-[4px] bg-[#102a3d] px-6 py-8 sm:px-[43px] sm:py-[38px]"
        >
            <img
                :src="asset('images/brand/contact-ghost-mark.svg')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute right-0 top-[18px] h-[198px] w-[201px] max-w-none"
            />
            <h3
                class="relative z-10 max-w-[397px] font-brand-display text-[30px] font-black italic uppercase leading-[0.95] tracking-[-0.01em] text-white md:text-[36px]"
            >
                Stay in the loop with Gift Hero!
            </h3>
            <ul class="relative z-10 flex flex-wrap gap-x-10 gap-y-3">
                <li v-for="handle in socialHandles" :key="handle.key">
                    <!--
                        Explicit color: legacy `a { color: … }` beats
                        inheritance on pages that load app.scss.
                    -->
                    <a
                        :href="social_links[handle.key]"
                        target="_blank"
                        rel="noopener"
                        :aria-label="handle.ariaLabel"
                        class="flex items-center gap-2.5 text-white transition hover:text-white/70"
                    >
                        <img
                            :src="asset(handle.icon)"
                            alt=""
                            class="size-[33px]"
                        />
                        <span
                            class="font-brand-display text-[20px] font-semibold italic uppercase leading-none tracking-[-0.01em]"
                        >
                            {{ handle.label }}
                        </span>
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>
